import { useState } from "react";
import { Button, Typography, message, Space, Card, Flex } from "antd";
import { PDFDocument } from "pdf-lib";
import { downloadBlob } from "./utils";
import GeneralUploader from "./Uploader";

const { Title, Text } = Typography;

export default function MergePDFs() {
    const [fileList, setFileList] = useState([]);
    const [merging, setMerging] = useState(false);
    const [mergedBytes, setMergedBytes] = useState(null);
    const [mergedFilename, setMergedFilename] = useState("");

    const resetAll = () => {
        setFileList([]);
        setMergedBytes(null);
        setMergedFilename("");
        setMerging(false);
    };

    const handleUploadChange = (newFileList) => {
        setFileList(newFileList || []);
    };

    const getActualFile = (file) => file?.originFileObj ?? file;

    const handleMerge = async () => {
        if ((fileList || []).length < 2) {
            message.error("Please upload at least two PDF files");
            return;
        }

        const key = "merge";
        message.loading({ content: "Merging PDFs...", key, duration: 0 });
        setMerging(true);

        try {
            const mergedDoc = await PDFDocument.create();

            for (const f of fileList) {
                const actual = getActualFile(f);
                if (!actual || typeof actual.arrayBuffer !== "function")
                    throw new Error(
                        "One of the uploaded items is not a valid file."
                    );

                const buffer = await actual.arrayBuffer();
                const src = await PDFDocument.load(buffer);
                const pages = await mergedDoc.copyPages(
                    src,
                    src.getPageIndices()
                );
                pages.forEach((p) => mergedDoc.addPage(p));
            }

            const bytes = await mergedDoc.save();
            const firstName =
                fileList[0]?.name ||
                fileList[0]?.originFileObj?.name ||
                "merged";
            setMergedBytes(bytes);
            setMergedFilename(
                `${firstName.replace(/\.pdf$/i, "")}\_merged.pdf`
            );

            message.success({ content: "PDFs merged successfully!", key });
        } catch (error) {
            message.error({
                content: `Error: ${error?.message || error}`,
                key,
            });
        } finally {
            setMerging(false);
        }
    };

    const handleDownload = () => {
        if (!mergedBytes) return;
        downloadBlob(mergedBytes, mergedFilename || "merged.pdf");
    };

    const handleStartOver = resetAll;

    return (
        <div style={{ maxWidth: 800, margin: "0 auto" }}>
            <Space
                direction="vertical"
                size={40}
                style={{
                    textAlign: "center",
                    padding: "40px 20px",
                    width: "100%",
                }}
            >
                <div>
                    <Title level={1}>Merge PDF Files</Title>
                    <Text>
                        Merge 2 or more PDF files into a single PDF file
                    </Text>
                </div>

                {!mergedBytes && (
                    <GeneralUploader
                        type="pdf"
                        multiple
                        fileList={fileList}
                        onChange={handleUploadChange}
                    />
                )}

                {fileList.length > 0 && !mergedBytes && (
                    <Button
                        type="primary"
                        size="large"
                        onClick={handleMerge}
                        style={{ marginTop: 12 }}
                        loading={merging}
                    >
                        Merge PDFs
                    </Button>
                )}

                {mergedBytes && (
                    <Card
                        size="small"
                        style={{ padding: 20, textAlign: "center" }}
                    >
                        <Title level={3} style={{ margin: 0 }}>
                            Your file is ready
                        </Title>

                        <Flex vertical gap={14} style={{ marginTop: 20 }}>
                            <Button
                                type="primary"
                                size="default"
                                onClick={handleDownload}
                                style={{ minWidth: 220 }}
                            >
                                Download
                            </Button>
                            <Button
                                size="default"
                                onClick={handleStartOver}
                                style={{ minWidth: 220 }}
                            >
                                Start Over
                            </Button>
                        </Flex>
                    </Card>
                )}
            </Space>
        </div>
    );
}
